<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

/**
 * Provider-agnostic ad placeholder.
 *
 * Renders nothing (zero height, no DOM noise) unless a provider is configured via
 * `VITE_AD_PROVIDER`, so development, tests and self-hosted builds are unaffected.
 * Per tenet 1, slots never sit above the calculator and never shift layout: the
 * reserved box keeps its size whether or not an ad fills it.
 */
const props = withDefaults(
  defineProps<{
    placement: 'leaderboard' | 'sidebar' | 'footer'
    /** Hide entirely below this viewport width (px). 0 means always show. */
    minViewport?: number
  }>(),
  { minViewport: 0 },
)

const { t } = useI18n()

const provider = import.meta.env.VITE_AD_PROVIDER ?? ''
const enabled = computed(() => provider !== '')

const sizes: Record<string, { width: number; height: number }> = {
  leaderboard: { width: 728, height: 90 },
  sidebar: { width: 300, height: 600 },
  footer: { width: 728, height: 90 },
}
const size = computed(() => sizes[props.placement] ?? sizes.leaderboard!)
</script>

<template>
  <aside
    v-if="enabled"
    class="ad-slot"
    :class="`ad-slot--${placement}`"
    :style="{
      '--ad-width': `${size.width}px`,
      '--ad-height': `${size.height}px`,
      '--ad-min-viewport': `${minViewport}px`,
    }"
    :aria-label="t('ads.label')"
  >
    <!-- TODO(phase-1): mount the configured provider's script here. Keep the
         reserved box so filling it cannot reflow the calculator. -->
    <div class="ad-slot__box" :data-placement="placement" :data-provider="provider"></div>
  </aside>
</template>

<style scoped>
.ad-slot {
  display: flex;
  justify-content: center;
  margin: var(--space-4) 0;
}

.ad-slot__box {
  width: min(100%, var(--ad-width));
  height: var(--ad-height);
  border: 1px dashed var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface-sunken);
}

/* Sidebar ads only make sense when there is room beside the table. */
.ad-slot--sidebar {
  display: none;
}

@media (min-width: 90rem) {
  .ad-slot--sidebar {
    display: flex;
  }
}
</style>
